// src/components/CensoredContent.jsx
import React, { useState, useMemo } from 'react';
import { censorText, parseCensoredText } from '../utils/profanityFilter';
import { useI18n } from '../i18n';
import { Eye, EyeOff, AlertTriangle } from 'lucide-react';

const CensoredContent = ({ text, className = '' }) => {
  const { t } = useI18n();
  const [isRevealed, setIsRevealed] = useState(false);

  const { censoredText, hasProfanity } = useMemo(() => {
    const result = censorText(text);
    return {
      censoredText: result.text,
      hasProfanity: result.hasProfanity
    };
  }, [text]);

  const segments = useMemo(() => {
    if (!hasProfanity || isRevealed) return null;
    return parseCensoredText(censoredText);
  }, [censoredText, hasProfanity, isRevealed]);

  // If no profanity or revealed, show original text
  if (!hasProfanity || isRevealed) {
    return (
      <div className={className}>
        <p className="post-content">{text}</p>
        {hasProfanity && isRevealed && (
          <button
            className="profanity-toggle revealed"
            onClick={() => setIsRevealed(false)}
            aria-label={t('censoredContent.hide')}
          >
            <EyeOff size={14} />
            {t('censoredContent.hide')}
          </button>
        )}
      </div>
    );
  }

  // Show censored content with warning
  return (
    <div className={className}>
      <div className="profanity-warning">
        <AlertTriangle size={14} />
        <span>{t('censoredContent.warning')}</span>
        <button
          className="profanity-reveal-btn"
          onClick={() => setIsRevealed(true)}
          aria-label={t('censoredContent.show')}
        >
          <Eye size={14} />
          {t('censoredContent.show')}
        </button>
      </div>
      <p className="post-content censored-text">
        {segments.map((segment, index) => (
          segment.type === 'censored' ? (
            <span 
              key={index} 
              className="blurred-word"
              title={t('censoredContent.show')}
            >
              {segment.content}
            </span>
          ) : (
            <span key={index}>{segment.content}</span>
          )
        ))}
      </p>
    </div>
  );
};

export default CensoredContent;
